import { FC, memo, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  Theme,
  useTranslation,
  useBreakpoint,
  Breakpoints,
  ButtonWidth,
  useNextgenRouter,
  Modal,
  ButtonAlignment,
} from '@nextgen-web-framework/all';
import { identityApi } from '@commerce-identity-webcomponents/sdk';
import { ConfigService, Logger, StorageService } from '@nextgen-web-framework/core';
import logObject from '../../../../logObject';
import type { LoginSessionDetectedModalProps } from './types/propsTypes';
import styles from './LoginSessionDetectedModal.module.scss';

export const LoginSessionDetectedModal: FC<LoginSessionDetectedModalProps> = ({ onClose }) => {
  const viewport = useBreakpoint() as Breakpoints;
  const buttonSize = [Breakpoints.MD].includes(viewport) ? ButtonSize.SMALL : ButtonSize.MEDIUM;
  const t = useTranslation();
  const router = useNextgenRouter();
  const logger = Logger.getInstance(logObject('LoginSessionDetectedModal', 'LoginSessionDetectedModal.tsx'));

  const [showModal, setShowModal] = useState<boolean>(true);

  const getLogoutProps = () => {
    // Reference: commerce-webcomponents\source\ui\utils\auth.ts
    const configs = ConfigService?.getConfigs();
    const baseUrl = ApiConfigService.getBaseUrl();
    const redirectUri = window.location.href;

    // After second logout, then redirect to sharelink
    const postAuthUrl = btoa(JSON.stringify({ redirectUri: redirectUri }));
    const logoutPath = String(configs.NEXT_PUBLIC_OIDC_API_URL).replace('auth', 'logout');
    return `${baseUrl}${logoutPath}?state=${postAuthUrl}`;
  };

  const onPrimaryButtonClick = async () => {
    const configs = ConfigService?.getConfigs();
    const localstorage = StorageService.getLocalStorage();
    const sessionstorage = StorageService.getSessionStorage();

    if (typeof window !== 'undefined') {
      try {
        // Redirect to current sharelink after logout
        const postLogoutRedirectUri = getLogoutProps();

        // Perform logout, we need 2 steps
        // 1. GLU logout
        // 2. OIDC logout in postLogoutRedirectUri
        const logoutRes = await identityApi.getLogoutUrl({
          gluuBaseUrl: `${configs.NEXT_PUBLIC_GLU_BASE_URL}`,
          postLogoutRedirectUri: postLogoutRedirectUri,
        });

        // Clear storage
        localstorage.clearStorage();
        sessionstorage.clearStorage();

        // replace the current route with the logout URL
        router?.replace?.(logoutRes);
      } catch (error) {
        logger.error(error);
      }
    }
  };

  const onSecondaryButtonClick = () => {
    if (typeof window !== 'undefined') {
      onClose?.();

      try {
        window.opener = null;
        window.open('', '_self');
        window.close();

        // If browser silently fails (no error), fallback after delay
        setTimeout(() => {
          if (!window.closed) {
            window.history.back();
          }
        }, 500);
      } catch (error) {
        logger.error(error);
        router.back();
      }

      setShowModal(false);
    }
  };

  if (!showModal) {
    return null;
  }

  return (
    <Modal
      closeIcon={false}
      headline={t('profile.ErrorCard.existingSession.title')}
      primaryButton={
        <Button
          buttonStyle={ButtonStyles.SOLID}
          onClick={onPrimaryButtonClick}
          size={buttonSize}
          theme={Theme.BLACK}
          buttonWidth={ButtonWidth.FULL_WIDTH}
          title={t('profile.ErrorCard.existingSession.primaryCta')}
          type="button"
          data-testId="primary-btn"
        />
      }
      primaryButtonLabel={''}
      buttonAlignment={ButtonAlignment.VERTICAL}
      secondaryButton={
        <Button
          buttonStyle={ButtonStyles.OUTLINE}
          onClick={onSecondaryButtonClick}
          size={buttonSize}
          theme={Theme.BLACK}
          buttonWidth={ButtonWidth.FULL_WIDTH}
          title={t('profile.ErrorCard.existingSession.secondaryCta')}
          type="button"
          data-testId="secondary-btn"
        />
      }
    >
      <div className={styles['error-card']} data-testid="error-card">
        {t('profile.ErrorCard.existingSession.desc')}
      </div>
    </Modal>
  );
};

export default memo(LoginSessionDetectedModal);
